import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SKILLS, skillValue, type SkillDef } from '@/domain/skills';
import { useGameStore } from '@/state/gameStore';
import type { SkillId } from '@/domain/types';
import './SkillSlots.css';

const SKILL_ICONS: Record<SkillId, string> = {
  execute: '⚡',
  momentum: '🔄',
  shield_bash: '🛡',
  kinetic_absorption: '🧲',
  market_manipulation: '📈',
  compounding_interest: '💹',
  treasure_hunter: '🎯',
  salvage_expert: '🔧',
  fireball: '🔥',
  lightning_bolt: '⚡',
  frost_lance: '❄',
  frost_nova: '❄',
  phantom_burst: '✦',
  engineer_overload: '⚙',
};

const SKILL_NAMES: Record<SkillId, string> = {
  execute: 'Execute',
  momentum: 'Momentum',
  shield_bash: 'Schild-Ramme',
  kinetic_absorption: 'Kinet. Absorption',
  market_manipulation: 'Markt-Manipulation',
  compounding_interest: 'Zinseszins',
  treasure_hunter: 'Schatzjäger',
  salvage_expert: 'Verwertungs-Experte',
  fireball: 'Feuerball',
  lightning_bolt: 'Blitzschlag',
  frost_lance: 'Eislanze',
  frost_nova: 'Frost-Nova',
  phantom_burst: 'Phantom-Stoß',
  engineer_overload: 'Überladung',
};

function formatValue(def: SkillDef, level: number): string {
  const v = skillValue(def, level);
  const id = def.id;
  if (id === 'execute') return `${(v * 100).toFixed(0)}% HP`;
  if (id === 'momentum') return `+${(v * 100).toFixed(0)}% Ladung/Kill`;
  if (id === 'shield_bash') return `${v.toFixed(1)}× DEF`;
  if (id === 'kinetic_absorption') return `+${v.toFixed(0)} DEF/Treffer`;
  if (id === 'market_manipulation') return `${v.toFixed(1)}× Miner`;
  if (id === 'compounding_interest') return `/${(v / 1000).toFixed(0)}k → +1%`;
  if (id === 'treasure_hunter') return `${Math.round(v)}× Boss-Loot`;
  if (id === 'salvage_expert') return `${(v * 100).toFixed(0)}% Rückgabe`;
  if (id === 'fireball' || id === 'lightning_bolt' || id === 'frost_lance') return `${v.toFixed(1)}× Schaden`;
  return `${v.toFixed(2)}`;
}

interface SkillSlotProps {
  slotIndex: 0 | 1;
  player: { level: number; skillSlots: [SkillId | null, SkillId | null]; unlockedSkills: SkillId[] };
  onActivateMarketManip?: () => void;
  marketManipActive: boolean;
}

function SkillSlot({ slotIndex, player, onActivateMarketManip, marketManipActive }: SkillSlotProps) {
  const setSkillSlot = useGameStore((s) => s.setSkillSlot);
  const [open, setOpen] = useState(false);
  // Trigger ref + measured rect — dropdown is portaled to body and
  // positioned with `position: fixed` so it can never be clipped by an
  // ancestor's clip-path / overflow:hidden / stacking-context.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Measure trigger position whenever the dropdown opens, plus on resize/scroll
  // so the panel stays anchored to the button.
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const recompute = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open]);

  // Outside-click closes
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const currentId = player.skillSlots[slotIndex];
  const slotType = slotIndex === 0 ? 'active' : 'passive';
  const currentDef = currentId ? SKILLS[currentId] : null;

  const available = Object.values(SKILLS).filter(
    (s) => s.type === slotType && player.unlockedSkills.includes(s.id),
  );
  const locked = Object.values(SKILLS).filter(
    (s) => s.type === slotType && !player.unlockedSkills.includes(s.id),
  );

  const isMarketManip = currentId === 'market_manipulation';

  return (
    <div className={`skill-slot skill-slot--${slotType} ${currentDef ? 'skill-slot--filled' : ''}`}>
      <div className="skill-slot__label">
        {slotType === 'active' ? '⚡ Aktiver Skill' : '◎ Passiver Skill'}
      </div>

      <button
        ref={triggerRef}
        className="skill-slot__trigger"
        onClick={() => setOpen((o) => !o)}
      >
        {currentDef ? (
          <>
            <span className="skill-slot__icon">{SKILL_ICONS[currentDef.id]}</span>
            <div className="skill-slot__info">
              <span className="skill-slot__name">{SKILL_NAMES[currentDef.id]}</span>
              <span className="skill-slot__value">{formatValue(currentDef, player.level)}</span>
            </div>
          </>
        ) : (
          <span className="skill-slot__empty">Kein Skill — tippen zum Auswählen</span>
        )}
        <span className="skill-slot__chevron">{open ? '▲' : '▼'}</span>
      </button>

      {isMarketManip && slotType === 'active' && (
        <button
          className={`btn btn--primary skill-slot__activate ${marketManipActive ? 'skill-slot__activate--active' : ''}`}
          onClick={onActivateMarketManip}
          disabled={marketManipActive}
        >
          {marketManipActive ? '📈 Aktiv!' : '📈 Aktivieren (60s)'}
        </button>
      )}

      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="skill-dropdown skill-dropdown--portaled"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
          }}
        >
          <button
            className="skill-option skill-option--none"
            onClick={() => { setSkillSlot(slotIndex, null); setOpen(false); }}
          >
            ✕ Kein Skill
          </button>
          {available.map((def) => (
            <button
              key={def.id}
              className={`skill-option ${currentId === def.id ? 'skill-option--active' : ''}`}
              onClick={() => { setSkillSlot(slotIndex, def.id); setOpen(false); }}
            >
              <span className="skill-option__icon">{SKILL_ICONS[def.id]}</span>
              <div className="skill-option__body">
                <span className="skill-option__name">{SKILL_NAMES[def.id]}</span>
                <span className="skill-option__val">{formatValue(def, player.level)}</span>
              </div>
            </button>
          ))}
          {locked.length > 0 && (
            <div className="skill-locked-header">Gesperrt</div>
          )}
          {locked.map((def) => (
            <div key={def.id} className="skill-option skill-option--locked">
              <span className="skill-option__icon">{SKILL_ICONS[def.id]}</span>
              <div className="skill-option__body">
                <span className="skill-option__name">{SKILL_NAMES[def.id]}</span>
                <span className="skill-option__val">Vault-Tier {def.unlockVaultTier}</span>
              </div>
              <span className="skill-option__lock">🔒</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

interface SkillSlotsProps {
  player: { level: number; skillSlots: [SkillId | null, SkillId | null]; unlockedSkills: SkillId[]; marketManipEndsAt: number };
}

export function SkillSlots({ player }: SkillSlotsProps) {
  const activateMarketManip = useGameStore((s) => s.activateMarketManip);
  const marketManipActive = player.marketManipEndsAt > Date.now();

  return (
    <div className="skill-slots">
      <div className="skill-slots__title">⚡ Skill-Slots</div>
      <SkillSlot
        slotIndex={0}
        player={player}
        marketManipActive={marketManipActive}
        onActivateMarketManip={activateMarketManip}
      />
      <SkillSlot
        slotIndex={1}
        player={player}
        marketManipActive={marketManipActive}
      />
    </div>
  );
}
